import { useState } from 'react';
import { Brain, Check, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card/Card';
import { Checkbox } from '@/components/ui/checkbox';
import { IconButton } from '@/components/ui/icon-button';
import { cn } from '@/lib/utils';
import { toolStateTone } from '@/lib/toolStateTone';
import { useTranslation } from '@/hooks/useTranslation';
import { useLabeling } from '../../context/useLabeling';
import { IMAGE_LABELS } from '../../types/LabelingTypes';
import type { ImageLabel, ToolImage, ToolState } from '../../types/LabelingTypes';
import { LABEL_BORDER, LABEL_FILL, LABEL_STRIP, LABEL_TEXT } from '../../utils/labelStateClasses';

interface GridCardProps {
  item: ToolImage;
}

interface CnnStripProps {
  item: ToolImage;
  stateLabelMap: Record<ToolState, string>;
  onAccept: () => void;
  onReject: () => void;
}

function CnnStrip({ item, stateLabelMap, onAccept, onReject }: CnnStripProps) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  if (!item.cnn || dismissed) return null;

  const state = item.cnn.mappedState;
  const pct = item.cnn.confidence !== undefined ? Math.round(item.cnn.confidence * 100) : null;

  const handleAccept = () => {
    setDismissed(true);
    onAccept();
  };
  const handleReject = () => {
    setDismissed(true);
    onReject();
  };

  return (
    <div className={cn('mx-2 mb-2 rounded-lg border overflow-hidden', LABEL_BORDER[state])}>
      <div className={cn('flex flex-col gap-1.5 py-1.5 pr-2 pl-3', LABEL_STRIP[state])}>
        {/* Row 1: badge + label + actions */}
        <div className="flex items-center gap-1.5">
          <Badge tone="primary" size="sm" caps className="font-mono">
            {t('labeling.cnn.tag')}
          </Badge>
          <span className={cn('flex-1 text-xs font-semibold truncate', LABEL_TEXT[state])}>
            {stateLabelMap[state]}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <IconButton
              icon={Check}
              variant="outline"
              label={t('labeling.cnn.accept')}
              onClick={handleAccept}
            />
            <IconButton icon={X} label={t('labeling.cnn.reject')} onClick={handleReject} />
          </div>
        </div>

        {/* Row 2: confidence bar or no-score label */}
        {pct !== null ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-[3px] rounded-full bg-border overflow-hidden">
              <div
                className={cn('h-full rounded-full transition-all', LABEL_FILL[state])}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-2xs font-mono text-muted-foreground shrink-0">{pct}%</span>
          </div>
        ) : (
          <span className="text-2xs font-mono text-muted-foreground">
            {t('labeling.cnn.noScore')}
          </span>
        )}
      </div>
    </div>
  );
}

export function GridCard({ item }: GridCardProps) {
  const { t } = useTranslation();
  const {
    selected,
    toggleSelect,
    jump,
    setMode,
    applyLabel,
    classifyWithAi,
    acceptSuggestion,
    rejectSuggestion,
    refreshImageUrl,
  } = useLabeling();

  const shortLabelMap: Record<ImageLabel, string> = {
    ok: t('labeling.state.shortOk'),
    worn: t('labeling.state.shortWorn'),
    unreadable: t('labeling.state.shortUnreadable'),
  };

  const stateLabelMap: Record<ImageLabel, string> = {
    ok: t('labeling.state.ok'),
    worn: t('labeling.state.worn'),
    unreadable: t('labeling.state.unreadable'),
  };

  return (
    <Card padding="none" className="overflow-hidden flex flex-col">
      {/* Thumbnail area */}
      <div className="relative">
        <div className="absolute top-2 left-2 z-10">
          <Checkbox
            checked={selected.has(item.key)}
            onCheckedChange={() => toggleSelect(item.key)}
            aria-label={t('labeling.grid.selectImage')}
          />
        </div>

        <button
          type="button"
          data-testid="thumbnail-btn"
          aria-label={t('labeling.grid.openImage')}
          className="block w-full aspect-[4/3] overflow-hidden outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50"
          onClick={() => {
            setMode('focus');
            jump(item.key);
          }}
        >
          <img
            src={item.imageUrl}
            alt=""
            data-testid="thumbnail-img"
            className="object-cover w-full h-full"
            onError={() => void refreshImageUrl(item.key)}
          />
        </button>

        {item.label && (
          <Badge tone={toolStateTone(item.label)} size="sm" className="absolute top-2 right-2">
            {shortLabelMap[item.label]}
          </Badge>
        )}
      </div>

      {/* Meta */}
      <div className="px-3 pt-2 pb-1">
        <p className="text-xs font-medium text-foreground truncate">{item.toolId}</p>
        <p className="text-2xs text-muted-foreground truncate">{item.ref}</p>
      </div>

      {/* Quick-label row + AI button */}
      <div className="px-2 pb-2 flex items-center gap-1">
        {IMAGE_LABELS.map((state) => (
          <button
            key={state}
            type="button"
            title={stateLabelMap[state]}
            aria-label={stateLabelMap[state]}
            aria-pressed={item.label === state}
            onClick={() => applyLabel(item.key, state)}
            className={cn(
              'w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
              LABEL_FILL[state],
              item.label === state ? 'border-foreground/40 scale-110' : 'border-transparent',
            )}
          />
        ))}
        <div className="flex-1" />
        <IconButton
          icon={Brain}
          label={t('labeling.cnn.button')}
          onClick={() => void classifyWithAi(item.key)}
        />
      </div>

      {/* CNN suggestion strip */}
      <CnnStrip
        item={item}
        stateLabelMap={stateLabelMap}
        onAccept={() => acceptSuggestion(item.key)}
        onReject={() => rejectSuggestion(item.key)}
      />
    </Card>
  );
}
